<template>
  <div class="p-location">
    <p-fieldset :label="$t('core.PLocation.location')">
      <p-row>
        <p-col v-if="vm.isInEditMode" xs12 sm8>
          <label class="text-field-label" :for="`${_uid}p-location-search-field`">{{ $t('core.app.search') }}</label>
          <p-icon
            class="searchicon ml-1"
            color="readonly"
          >
            search
          </p-icon>
          <input
            :id="`${_uid}p-location-search-field`"
            ref="searchField"
            class="p-location-search-field"
            type="text"
            :placeholder="`${$t('core.PLocation.location')}...`"
          >
        </p-col>
      </p-row>
      <p-row>
        <p-col class="map" xs12 sm8>
          <div v-if="vm.apiKey !== '' && vm.googleMapsMapId !== '' && vm.googleMapsVersion !== ''" ref="pLocationMap" :class="vm.isInEditMode ? 'enlargedMap' : 'previewMap'">
          </div>
          <div v-else>
            <p-alert :value="true" type="error">
              {{ $t('core.PLocation.errorAlert', { missingConfig: missingConfigPropsErrorMessage }) }}
            </p-alert>
          </div>
        </p-col>
        <p-col xs12 sm4 class="controls">
          <p-row v-if="!vm.isInEditMode" class="viewControls fill-height" column>
            <p-col class="py-0" shrink align-start>
              <p-row column no-gutters>
                <p-col>
                  <p-number-field
                    v-model="vm.latitude"
                    :label="$t('core.PLocation.latitude')"
                    :disabled="true"
                    no-steppers
                  ></p-number-field>
                  <p-number-field
                    v-model="vm.longitude"
                    :label="$t('core.PLocation.longitude')"
                    :disabled="true"
                    no-steppers
                  ></p-number-field>
                </p-col>
              </p-row>
            </p-col>
            <p-col class="py-0" grow align-end justify-end>
              <p-button
                class="mt-4"
                :secondary="false"
                @click="setEditMode()"
              >
                {{ $t('core.app.edit') }}
              </p-button>
            </p-col>
          </p-row>
          <p-row v-else class="editControls fill-height" column>
            <p-col class="py-0 numberFieldsContainer" shrink align-start>
              <p-row column no-gutters>
                <p-col>
                  <p-number-field
                    v-model="vm.latitude"
                    :label="$t('core.PLocation.latitude')"
                    :max="85"
                    :min="-85"
                    no-steppers
                    @change="vm.updateGoogleMap()"
                  ></p-number-field>
                </p-col>
                <p-col>
                  <p-number-field
                    v-model="vm.longitude"
                    :label="$t('core.PLocation.longitude')"
                    :max="180"
                    :min="-180"
                    no-steppers
                    @change="vm.updateGoogleMap()"
                  ></p-number-field>
                </p-col>
              </p-row>
            </p-col>
            <p-col class="py-0 buttonsContainer" grow align-end justify-end>
              <p-button
                :secondary="true"
                @click="vm.onCancel()"
              >
                {{ $t('core.app.cancel') }}
              </p-button>
              <p-button
                class="ml-1 mt-1"
                :secondary="false"
                @click="onSave()"
              >
                {{ $t('core.app.save') }}
              </p-button>
            </p-col>
          </p-row>
        </p-col>
      </p-row>
    </p-fieldset>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import PLocationViewModel from './p-location.vue.model';

  export default Vue.extend({
    name: 'PLocation',
    props: {
      defaultLatitude: { default: 0, type: Number },
      defaultLongitude: { default: 0, type: Number },
      zoom: { default: 18, type: Number },
      setMarker: { default: true, type: Boolean },
    },
    data: () => ({
      vm: new PLocationViewModel(),
      isDefaultLatitudeSet: false as boolean,
      isDefaultLongitudeSet: false as boolean,
    }),
    computed: {
      missingConfigPropsErrorMessage() {
        const missingConfigs = [];
        if (this.vm.apiKey === '') {
          missingConfigs.push('Google API Key');
        }
        if (this.vm.googleMapsMapId === '') {
          missingConfigs.push('Google Map Id');
        }
        if (this.vm.googleMapsVersion === '') {
          missingConfigs.push('Google Map Version');
        }
        return missingConfigs.join(', ');
      },
    },
    watch: {
      defaultLatitude: {
        immediate: true,
        handler(val) {
          if (!this.isDefaultLatitudeSet) {
            this.vm.latitude = val;
            this.vm.updateGoogleMap();
            this.isDefaultLatitudeSet = true;
          }
        },
      },
      defaultLongitude: {
        immediate: true,
        handler(val) {
          if (!this.isDefaultLongitudeSet) {
            this.vm.longitude = val;
            this.vm.updateGoogleMap();
            this.isDefaultLongitudeSet = true;
          }
        },
      },
    },
    mounted() {
      this.vm.googleWindow = window.google;
      this.vm.initializeGoogleMapsConfigruations();
      this.vm.zoom = this.zoom;
      this.vm.setMarker = this.setMarker;

      this.$nextTick(() => {
        this.vm.mapHtmlElement = this.$refs.pLocationMap as HTMLElement;
        this.vm.initializeGoogleMaps();
      });
    },
    methods: {
      setEditMode() {
        this.vm.isInEditMode = true;
        this.vm.previousLatitude = this.vm.latitude;
        this.vm.previousLongitude = this.vm.longitude;
        Vue.nextTick(() => {
          this.vm.map.setOptions({ disableDefaultUI: false });
          this.setupMapSearch();
        });
      },
      onSave() {
        this.vm.updateGoogleMap();
        this.vm.setViewMode();
        this.$emit('latitudeChange', this.vm.latitude);
        this.$emit('longitudeChange', this.vm.longitude);
      },
      setupMapSearch() {
        this.vm.searchBox = new this.vm.googleWindow.maps.places.SearchBox(this.$refs.searchField as HTMLInputElement);

        this.vm.googleWindow.maps.event.addListener(this.vm.searchBox, 'places_changed', () => {
          const searchResult = this.vm.searchBox.getPlaces();

          if (searchResult === undefined || searchResult.length === 0) {
            return;
          }

          const searchResultLat = searchResult[0].geometry?.location?.lat();
          const searchResultLng = searchResult[0].geometry?.location?.lng();

          if (searchResultLat && searchResultLng) {
            const panPoint = new this.vm.googleWindow.maps.LatLng(searchResultLat, searchResultLng);
            this.vm.map.panTo(panPoint);
          }
        });
      },
    },
  });
</script>
